// src/components/EditRegistrationModal.tsx
import React, { useState, useEffect } from "react";
import {
  updateRegistration,
  canChangeWeight,
  canChangeWeightCount,
  weightChangesUsed,
  weightChangesRemaining,
  MAX_WEIGHT_CHANGES,
  WEIGHT_CHANGE_COOLDOWN_MS,
  Registration,
} from "../firebase/registrations";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { C, FONT } from "../theme";
import { LockIcon, InfoIcon, ClockIcon, AlertTriangleIcon } from "./Icons";
import { getRecommendedWeights } from "../utils/weightSuggestions";
import { formatDate } from "../utils/dateHelpers";
import WeightSelect from "./WeightSelect";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  registration: Registration & { id: string };
  /** Weight categories available for this competition, keyed by division */
  weightCategories: { male: string[]; female: string[] };
  /** Category types available (e.g. ["U15", "U18", "Senior"]) */
  types: string[];
  /**
   * UI scope. "admin" exposes every field; "coach" only allows weight +
   * division and adds a deadline notice. Defaults to "admin" so existing
   * admin callers keep working without an explicit prop.
   */
  mode?: "admin" | "coach";
  /**
   * When provided (coach mode), the deadline after which edits are blocked.
   * If `null` or in the past, the modal renders read-only with a banner.
   */
  weightChangeDeadline?: Date | null;
  onClose: () => void;
  onSaved: (updated: Partial<Registration>) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditRegistrationModal({
  registration,
  weightCategories,
  types,
  mode = "admin",
  weightChangeDeadline = null,
  onClose,
  onSaved,
}: Props) {
  const { currentUser, userRole } = useAuth();
  const { t, language } = useTranslation();
  const isCoachMode = mode === "coach";

  // Deadline gate — only meaningful in coach mode. Admin mode never locks.
  const deadlinePassed =
    isCoachMode &&
    (!weightChangeDeadline || weightChangeDeadline.getTime() <= Date.now());

  // Live clock — drives the cooldown countdown and auto-re-enables Save the
  // moment the hour elapses, without the coach having to reopen the modal.
  const [now, setNow] = useState(() => new Date());

  // When (if ever) this athlete's weight was last changed, as a Date.
  const lastChangedDate: Date | null = (() => {
    const last: any = (registration as any).weightLastChanged;
    if (!last) return null;
    const d: Date = typeof last.toDate === "function" ? last.toDate() : new Date(last);
    return isNaN(d.getTime()) ? null : d;
  })();

  // Hard cap: a coach may change this athlete's weight at most
  // MAX_WEIGHT_CHANGES times. Admins are exempt. Terminal — unlike the
  // cooldown, waiting doesn't help once it's reached.
  const changesUsed = weightChangesUsed(registration);
  const changesLeft = weightChangesRemaining(registration);
  const limitReached =
    isCoachMode && !deadlinePassed && !canChangeWeightCount(registration);

  // Once-per-hour rate limit on coach weight edits. Admins are exempt. We
  // don't surface it once the hard cap is hit (the cap message takes over).
  const rateLimited =
    isCoachMode && !deadlinePassed && !limitReached && !canChangeWeight(registration, now);

  // Milliseconds left on the cooldown (clamped at 0).
  const msRemaining = lastChangedDate
    ? Math.max(0, lastChangedDate.getTime() + WEIGHT_CHANGE_COOLDOWN_MS - now.getTime())
    : 0;

  // Tick every second, but only while the cooldown is actually counting down.
  useEffect(() => {
    if (!rateLimited) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [rateLimited]);

  // Live "42 min 09 sec" / "47 sec" countdown, localized units.
  const countdownLabel = (() => {
    if (msRemaining <= 0) return "";
    const totalSec = Math.ceil(msRemaining / 1000);
    const m = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    const minU = t("editReg.unitMin");
    const secU = t("editReg.unitSec");
    return m > 0
      ? `${m} ${minU} ${String(sec).padStart(2, "0")} ${secU}`
      : `${sec} ${secU}`;
  })();

  // Clock time the cooldown ends, e.g. "14:30".
  const nextAllowedLabel = lastChangedDate
    ? new Date(lastChangedDate.getTime() + WEIGHT_CHANGE_COOLDOWN_MS).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const [club, setClub] = useState(registration.club);
  const [division, setDivision] = useState<"Male" | "Female">(registration.division);
  const [category, setCategory] = useState(registration.category ?? "");
  const [weightCategory, setWeightCategory] = useState(registration.weightCategory);
  const [price, setPrice] = useState<number>(registration.price);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // When division changes, reset weight if it's no longer valid
  useEffect(() => {
    const available = division === "Male" ? weightCategories.male : weightCategories.female;
    if (!available.includes(weightCategory)) {
      setWeightCategory(available[0] ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [division]);

  const availableWeights =
    division === "Male" ? weightCategories.male : weightCategories.female;

  const handleSave = async () => {
    setError("");
    if (deadlinePassed) {
      setError(t("editReg.error.deadlinePassed"));
      return;
    }
    // Coach-only hard cap on total weight changes (admins bypass).
    if (limitReached) {
      setError(t("editReg.error.limitReached").replace("{max}", String(MAX_WEIGHT_CHANGES)));
      return;
    }
    // Coach-only once-per-hour rate limit (admins bypass).
    if (rateLimited) {
      setError(t("editReg.error.rateLimited"));
      return;
    }
    if (!currentUser) {
      setError(t("editReg.error.signedIn"));
      return;
    }

    // Coach mode only edits weight; division/category/club/price stay frozen
    // (division is federation-controlled, the rest are admin-only).
    if (isCoachMode) {
      if (!weightCategory) {
        setError(t("editReg.error.weightRequired"));
        return;
      }
    } else {
      if (!club.trim()) { setError(t("editReg.error.clubRequired")); return; }
      if (!weightCategory) { setError(t("editReg.error.weightRequired")); return; }
      if (!category) { setError(t("editReg.error.categoryRequired")); return; }
    }

    setSaving(true);
    try {
      // Coach updates touch ONLY weightCategory — matching the Firestore rule's
      // allowed key set (plus the weightLastChanged/weightChangedBy stamps that
      // updateRegistration adds server-side).
      const updates: Partial<Registration> = isCoachMode
        ? { weightCategory }
        : { club: club.trim(), division, weightCategory, category, price };

      await updateRegistration(registration.id, updates, {
        editorUid: currentUser.uid,
        editorEmail: currentUser.email ?? "",
        editorName: currentUser.displayName ?? "",
        // Admin role may be either "admin" or "ranking_admin"; we record any
        // non-coach as "admin" since both have privileged write access.
        changedBy: userRole === "coach" ? "coach" : "admin",
      });
      onSaved(updates);
      onClose();
    } catch (err) {
      console.error("Failed to update registration:", err);
      setError(t("editReg.error.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const deadlineLabel = formatDate(weightChangeDeadline, language);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.label}>
              {isCoachMode ? t("editReg.coachTitle") : t("editReg.adminTitle")}
            </div>
            <div style={s.title}>
              {registration.athleteSnapshot.firstName}{" "}
              {registration.athleteSnapshot.lastName}
            </div>
            <div style={s.subtitle}>
              {t("editReg.ref")}{" "}
              <span style={s.refCode}>{registration.referenceCode}</span>
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.body}>
          {/* Coach-mode deadline banner */}
          {isCoachMode && !deadlinePassed && (
            <div style={s.deadlineNote}>
              <ClockIcon size={15} color="var(--color-warning)" style={{ marginTop: 1 }} />
              <span>{t("editReg.deadlineNote").replace("{date}", deadlineLabel)}</span>
            </div>
          )}
          {isCoachMode && deadlinePassed && (
            <div style={s.deadlinePassedBanner}>
              <LockIcon size={16} color={C.red} style={{ marginTop: 1 }} />
              <span>{t("editReg.deadlinePassedBanner").replace("{date}", deadlineLabel)}</span>
            </div>
          )}
          {/* Coach-mode change-budget counter — how many weight changes remain. */}
          {isCoachMode && !deadlinePassed && !limitReached && (
            <div style={s.changeCounter}>
              <InfoIcon size={14} color={C.text3} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>
                {t("editReg.changeCounter")
                  .replace("{used}", String(changesUsed))
                  .replace("{max}", String(MAX_WEIGHT_CHANGES))}
              </span>
              <span style={changesLeft <= 1 ? s.changeChipLow : s.changeChip}>
                {t("editReg.changeCounterLeft").replace("{left}", String(changesLeft))}
              </span>
            </div>
          )}
          {/* Coach-mode hard-cap banner — terminal, no Save once reached. */}
          {limitReached && (
            <div style={s.limitBanner}>
              <LockIcon size={16} color={C.red} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>
                <strong>
                  {t("editReg.limitReached").replace("{max}", String(MAX_WEIGHT_CHANGES))}
                </strong>
                <span style={s.limitBannerSub}> {t("editReg.limitReachedNote")}</span>
              </span>
            </div>
          )}
          {/* Coach-mode rate-limit banner (once per hour) — live countdown so
              the coach can see exactly why Save is locked and how long is left. */}
          {rateLimited && (
            <div style={s.rateLimitBanner}>
              <div style={s.rateLimitHead}>
                <AlertTriangleIcon size={20} color="var(--color-warning)" style={{ marginTop: 1, flexShrink: 0 }} />
                <strong style={s.rateLimitTitle}>{t("editReg.rateLimited")}</strong>
              </div>
              {countdownLabel && (
                <div style={s.rateLimitCountdown}>
                  <ClockIcon size={15} color="var(--color-warning)" />
                  <span>
                    {t("editReg.rateLimitedIn").replace("{time}", countdownLabel)}
                  </span>
                </div>
              )}
              {nextAllowedLabel && (
                <div style={s.rateLimitSub}>
                  {t("editReg.rateLimitedNext").replace("{time}", nextAllowedLabel)}
                </div>
              )}
            </div>
          )}

          {/* Read-only info */}
          <div style={s.infoRow}>
            <div style={s.infoItem}>
              <span style={s.infoLabel}>{t("editReg.competition")}</span>
              <span style={s.infoValue}>{registration.competitionName}</span>
            </div>
            <div style={s.infoItem}>
              <span style={s.infoLabel}>{t("editReg.dob")}</span>
              <span style={s.infoValue}>
                {registration.athleteSnapshot.dateOfBirth}
              </span>
            </div>
            {isCoachMode && (
              <>
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>{t("editReg.club")}</span>
                  <span style={s.infoValue}>{registration.club}</span>
                </div>
                <div style={s.infoItem}>
                  <span style={s.infoLabel}>{t("editReg.category")}</span>
                  <span style={s.infoValue}>{registration.category || "—"}</span>
                </div>
              </>
            )}
          </div>

          <div style={s.divider} />

          {/* Editable fields */}
          <div style={s.fieldGrid}>
            {/* Club — admin only */}
            {!isCoachMode && (
              <div style={s.field}>
                <label style={s.fieldLabel}>{t("editReg.club")}</label>
                <input
                  style={s.input}
                  value={club}
                  onChange={(e) => setClub(e.target.value)}
                  placeholder="Club name"
                />
              </div>
            )}

            {/* Division — federation-controlled; read-only for coaches */}
            <div style={s.field}>
              <label style={s.fieldLabel}>
                {t("editReg.division")}
                {isCoachMode && (
                  <span style={s.lockedTag}>
                    <LockIcon size={11} />
                    {t("editReg.divisionLocked")}
                  </span>
                )}
              </label>
              <select
                style={{ ...s.select, ...(isCoachMode ? s.selectDisabled : {}) }}
                value={division}
                onChange={(e) => setDivision(e.target.value as "Male" | "Female")}
                disabled={deadlinePassed || isCoachMode}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            {/* Category — admin only */}
            {!isCoachMode && (
              <div style={s.field}>
                <label style={s.fieldLabel}>{t("editReg.category")}</label>
                <select
                  style={s.select}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">— select —</option>
                  {types.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Weight Category */}
            <div style={s.field}>
              <label style={s.fieldLabel}>{t("editReg.weightCategory")}</label>
              <WeightSelect
                value={weightCategory}
                onChange={setWeightCategory}
                weights={availableWeights}
                recommended={getRecommendedWeights(category, division)}
                category={category}
                division={division}
                placeholder="— select —"
                selectStyle={s.select}
                disabled={deadlinePassed || limitReached}
                t={t}
              />
            </div>

            {/* Price — admin only */}
            {!isCoachMode && (
              <div style={s.field}>
                <label style={s.fieldLabel}>{t("editReg.price")}</label>
                <input
                  style={s.input}
                  type="number"
                  min={0}
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                />
              </div>
            )}
          </div>

          {/* Coach-mode note: category (age group) is federation-managed and
              intentionally not editable here — prevents ranking-bracket fraud. */}
          {isCoachMode && (
            <div style={s.categoryNote}>
              <InfoIcon size={14} color={C.text3} style={{ marginTop: 1 }} />
              <span>{t("editReg.categoryNote")}</span>
            </div>
          )}

          {error && <div style={s.error}>{error}</div>}

          <div style={s.divider} />

          {/* Actions */}
          <div style={s.actions}>
            <button style={s.cancelBtn} onClick={onClose} disabled={saving}>
              {deadlinePassed || limitReached ? t("editReg.close") : t("editReg.cancel")}
            </button>
            {!deadlinePassed && !limitReached && (
              <button
                style={saving || rateLimited ? s.saveBtnDisabled : s.saveBtn}
                onClick={handleSave}
                disabled={saving || rateLimited}
              >
                {saving ? t("editReg.saving") : t("editReg.save")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "14px",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "22px 24px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  label: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "4px",
  },
  title: {
    fontFamily: FONT.display,
    fontSize: "22px",
    color: C.text,
    letterSpacing: "0.5px",
    lineHeight: 1.1,
    marginBottom: "4px",
  },
  subtitle: { fontSize: "12px", color: C.text3 },
  refCode: {
    fontFamily: "monospace",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "4px",
    padding: "1px 6px",
    color: C.text,
    fontWeight: 600,
  },
  closeBtn: {
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    color: C.text3,
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
    flexShrink: 0,
  },
  body: { padding: "20px 24px 24px" },
  deadlineNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    backgroundColor: "var(--color-warning-soft)",
    border: `1px solid ${C.warningBorder}`,
    color: "var(--color-warning)",
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "12px",
    marginBottom: "16px",
    lineHeight: 1.5,
  },
  deadlinePassedBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    color: C.red,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "16px",
    lineHeight: 1.5,
  },
  rateLimitBanner: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    backgroundColor: "var(--color-warning-soft)",
    border: `2px solid ${C.warningBorder}`,
    color: "var(--color-warning)",
    borderRadius: "10px",
    padding: "14px 16px",
    fontSize: "13px",
    marginBottom: "16px",
    lineHeight: 1.5,
    boxShadow: "0 2px 10px rgba(245,158,11,0.18)",
  },
  rateLimitHead: { display: "flex", alignItems: "flex-start", gap: "8px" },
  rateLimitTitle: { fontSize: "14px", fontWeight: 700 },
  rateLimitCountdown: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    backgroundColor: "var(--color-warning-soft)",
    border: `1px solid ${C.warningBorder}`,
    borderRadius: "7px",
    padding: "8px 12px",
    fontSize: "16px",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    color: "var(--color-warning)",
  },
  rateLimitSub: { fontWeight: 400, color: "var(--color-warning)", fontSize: "12px" },
  changeCounter: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "9px 14px",
    fontSize: "12.5px",
    color: C.text2,
    marginBottom: "16px",
  },
  changeChip: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.3px",
    color: "var(--color-success)",
    backgroundColor: "rgba(34,197,94,0.14)",
    border: "1px solid rgba(34,197,94,0.4)",
    borderRadius: "20px",
    padding: "3px 10px",
    flexShrink: 0,
  },
  changeChipLow: {
    marginLeft: "auto",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.3px",
    color: "var(--color-warning)",
    backgroundColor: "var(--color-warning-soft)",
    border: `1px solid ${C.warningBorder}`,
    borderRadius: "20px",
    padding: "3px 10px",
    flexShrink: 0,
  },
  limitBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    color: C.red,
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "16px",
    lineHeight: 1.5,
  },
  limitBannerSub: { fontWeight: 400, color: C.text2 },
  lockedTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    marginLeft: "6px",
    color: C.text3,
    fontSize: "9px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  selectDisabled: {
    backgroundColor: C.surface,
    color: C.text3,
    cursor: "not-allowed",
    opacity: 0.85,
  },
  infoRow: { display: "flex", gap: "24px", flexWrap: "wrap" },
  infoItem: { display: "flex", flexDirection: "column", gap: "2px" },
  infoLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  infoValue: { fontSize: "13px", color: C.text2 },
  divider: { height: "1px", backgroundColor: C.border, margin: "16px 0" },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
  },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  fieldLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  input: {
    padding: "11px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "16px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    boxSizing: "border-box",
    width: "100%",
  },
  select: {
    padding: "11px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "16px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
    width: "100%",
  },

  categoryNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginTop: "14px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "7px",
    padding: "9px 12px",
    fontSize: "12px",
    color: C.text2,
    lineHeight: 1.5,
  },
  error: {
    marginTop: "12px",
    backgroundColor: C.redDim,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
  },
  actions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  cancelBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500,
  },
  saveBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  saveBtnDisabled: {
    backgroundColor: C.border,
    color: C.text3,
    border: "none",
    borderRadius: "7px",
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
};
